import type { JSX } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";

import { useGetServiceApiUserProfileQuery } from "@/api/enhancedGeneratedApi";
import {
  SprengnetterValuationHubValuationReportModelResponseAvmPriceResponse as AvmPriceResponse,
  GetServiceApiValuationReportRealEstateContactApiResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebPriceResponse,
  SprengnetterValuationHubApiModelResponseUserProfileResponse,
  ValuationHubCoreModelDtoContactInformationDto,
  ValuationHubCoreModelDtoValuationReportConfigurationDto,
  ValuationHubCoreModelDtoValuationReportImageDto,
  ValuationHubCoreModelEnumsValuationType,
  ValuationHubCoreModelImage,
} from "@/api/generatedApi";
import { getAvmDisplayedRange } from "@/helpers/avm";
import { getProsaWebMarketValue } from "@/helpers/prosaWeb";
import { useModuleContext } from "@/hooks/useModuleContext";
import { updateReportConfigurationDraft as updateCurrentValuationReportConfigurationDraft } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { updateReportConfigurationDraft as updateValuationReportConfigurationDraft } from "@/pages/valuationReport/valuationReportPage/valuationReportSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

interface FormDataContextType {
  formData: ValuationHubCoreModelDtoValuationReportConfigurationDto;
  setFormData: React.Dispatch<React.SetStateAction<ValuationHubCoreModelDtoValuationReportConfigurationDto>>;
}

const FormDataContext = createContext<FormDataContextType | undefined>(undefined);

export type ValuationReportConfigWithImageUrls = Partial<ValuationHubCoreModelDtoValuationReportConfigurationDto> & {
  company_logo_url?: string | null;
  profile_picture_url?: string | null;
};

interface ValuationReportConfigFormDataProviderProps {
  children: React.ReactNode;
  prosaWebPriceResult?: ProsaWebPriceResponse | null;
  avmPriceResult: AvmPriceResponse | null;
  images?: ValuationHubCoreModelImage[] | null;
  contact: GetServiceApiValuationReportRealEstateContactApiResponse | null;
  initialReport?: ValuationReportConfigWithImageUrls;
  skipUserProfileDefaults?: boolean;
}

const getStringValue = (value?: string | null): string => value ?? "";
const cloneReportConfiguration = (
  value: ValuationHubCoreModelDtoValuationReportConfigurationDto,
): ValuationHubCoreModelDtoValuationReportConfigurationDto => structuredClone(value);

const isContactInformationEmpty = (
  contactInformation?: ValuationHubCoreModelDtoContactInformationDto | null,
): boolean =>
  !contactInformation ||
  [
    contactInformation.company_name,
    contactInformation.email,
    contactInformation.phone_number,
    contactInformation.name,
    contactInformation.house_number,
    contactInformation.street,
    contactInformation.town,
    contactInformation.post_code,
    contactInformation.website,
  ].every((value) => !value || value.trim() === "");

export const ValuationReportConfigFormDataProvider = ({
  children,
  prosaWebPriceResult,
  avmPriceResult,
  images,
  contact,
  initialReport,
  skipUserProfileDefaults = false,
}: ValuationReportConfigFormDataProviderProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { isValuationReport } = useModuleContext();
  const reportSelectedEquipmentPriceRange = useAppSelector(
    (state) => state.valuationReport.selectedEquipmentPriceRange,
  );
  const propertyInformation = useAppSelector((state) =>
    isValuationReport ? state.valuationReport.propertyInformation : state.currentValuation.propertyInformation,
  );

  const prosaWebMarketValue = getProsaWebMarketValue(prosaWebPriceResult, {
    objectType: propertyInformation?.generalInfo.objectType,
    livingArea: propertyInformation?.buildingData.livingArea,
  });
  const avmDisplayedRange = getAvmDisplayedRange(
    avmPriceResult,
    isValuationReport ? reportSelectedEquipmentPriceRange : undefined,
  );
  const getDefaultValuationType = useCallback((): ValuationHubCoreModelEnumsValuationType => {
    if (prosaWebMarketValue != null && !!avmDisplayedRange.min && !!avmDisplayedRange.max) {
      return "ALL";
    }
    if (prosaWebMarketValue != null) {
      return "PROSAWEB";
    }
    return "AVM";
  }, [avmDisplayedRange.max, avmDisplayedRange.min, prosaWebMarketValue]);

  const reportConfigurationDraft = useAppSelector((state) =>
    isValuationReport
      ? state.valuationReport.reportConfigurationDraft
      : state.currentValuation.reportConfigurationDraft,
  );
  const { data: userProfileResponse } = useGetServiceApiUserProfileQuery(undefined, {
    skip: isValuationReport || skipUserProfileDefaults,
  });
  const defaultPrimaryColor = isValuationReport ? "#18EFC4" : "#1F497D";
  const defaultSecondaryColor = isValuationReport ? "#4A4A49" : "#FFFFFF";
  const userProfile = skipUserProfileDefaults
    ? undefined
    : (userProfileResponse?.value as SprengnetterValuationHubApiModelResponseUserProfileResponse | null | undefined) ??
      (userProfileResponse as unknown as SprengnetterValuationHubApiModelResponseUserProfileResponse | undefined);

  const buildContactFromUserProfile = useCallback((): ValuationHubCoreModelDtoContactInformationDto | null => {
    const profile = userProfile;
    if (!profile) return null;
    const name = `${getStringValue(profile.first_name)} ${getStringValue(profile.last_name)}`.trim();

    return {
      company_name: getStringValue(profile.company_name),
      email: getStringValue(profile.email),
      phone_number: getStringValue(profile.phone_number),
      name,
      house_number: getStringValue(profile.house_number),
      street: getStringValue(profile.street),
      town: getStringValue(profile.town),
      post_code: getStringValue(profile.post_code),
      website: getStringValue(profile.website),
    };
  }, [userProfile]);
  const profileCompanyLogo = userProfile?.company_logo_url;
  const profilePicture = userProfile?.profile_picture_url;
  const profilePrimaryColor = userProfile?.primary_color;
  const hasValuationContact = Boolean(contact) && !isContactInformationEmpty(contact);
  const reportImages = useMemo<ValuationHubCoreModelDtoValuationReportImageDto[] | null | undefined>(
    () =>
      images?.map((image) => ({
        url: image.url ?? null,
        title: image.title ?? null,
        is_title_picture: image.is_profile_picture ?? false,
      })),
    [images],
  );

  const initialFormData = useMemo(() => {
    const defaultContact = (hasValuationContact ? contact : buildContactFromUserProfile()) || {
      company_name: "",
      email: "",
      phone_number: "",
      name: "",
      house_number: "",
      street: "",
      town: "",
      post_code: "",
      website: "",
    };

    return {
      valuation_type: getDefaultValuationType(),
      title: undefined,
      reference_number: undefined,
      real_estate_images: reportImages,
      comments: initialReport?.comments,
      company_logo: initialReport?.company_logo ?? initialReport?.company_logo_url ?? profileCompanyLogo,
      profile_picture: initialReport?.profile_picture ?? initialReport?.profile_picture_url ?? profilePicture,
      primary_color: initialReport?.primary_color ?? profilePrimaryColor ?? defaultPrimaryColor,
      secondary_color: defaultSecondaryColor,
      contact_information: {
        ...defaultContact,
        ...(initialReport?.contact_information ?? {}),
      },
    };
  }, [
    buildContactFromUserProfile,
    contact,
    defaultPrimaryColor,
    defaultSecondaryColor,
    getDefaultValuationType,
    hasValuationContact,
    reportImages,
    initialReport,
    profileCompanyLogo,
    profilePicture,
    profilePrimaryColor,
  ]);
  const formData = useMemo(
    () => cloneReportConfiguration(reportConfigurationDraft ?? initialFormData),
    [initialFormData, reportConfigurationDraft],
  );

  const setFormData: React.Dispatch<React.SetStateAction<ValuationHubCoreModelDtoValuationReportConfigurationDto>> =
    useCallback(
      (value) => {
        const nextValue = typeof value === "function" ? value(formData) : value;
        const clonedNextValue = cloneReportConfiguration(nextValue);

        if (JSON.stringify(clonedNextValue) === JSON.stringify(formData)) {
          return;
        }

        dispatch(
          isValuationReport
            ? updateValuationReportConfigurationDraft(clonedNextValue)
            : updateCurrentValuationReportConfigurationDraft(clonedNextValue),
        );
      },
      [dispatch, formData, isValuationReport],
    );

  return <FormDataContext.Provider value={{ formData, setFormData }}>{children}</FormDataContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useValuationReportConfigFormData = (): FormDataContextType => {
  const context = useContext(FormDataContext);
  if (!context) {
    throw new Error("useFormData must be used within a FormDataProvider");
  }
  return context;
};
